import { fromZonedTime } from 'date-fns-tz';

import {
    DAY_NAMES,
    MINUTES_PER_DAY,
    MINUTES_PER_WEEK,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_constants';
import { formatWeeklyTimeRange } from '@apps/web/components/caregivers/caregiver_availability_schedule_formatting';
import {
    type AvailabilityStatus,
    type RawTimeboxedAvailability,
    type RawWeeklyAvailability,
    type TimeboxedAvailabilityEntry,
    type TimeboxedAvailabilityItem,
    type WeeklyAvailabilityEntry,
    type WeeklyAvailabilityEntrySegment,
    type WeeklyItem,
    type WeeklyScheduleSegment,
} from '@apps/web/components/caregivers/caregiver_availability_schedule_types';
import {
    getAvailabilitySourceLabel,
    getCollapsedSourceLabel,
} from '@apps/web/components/caregivers/caregiver_availability_source_labels';
import { mergeTimeboxedAvailabilityEntries } from '@apps/web/components/caregivers/caregiver_availability_timeboxed_model';
import { mondayStartDateKey, shiftDateKey } from '@apps/web/lib/date_utils';

export function mergeWeeklyAvailability(
    weeklyAvailability: ReadonlyArray<RawWeeklyAvailability>,
    externalSourceLabel: string | undefined,
): Array<WeeklyItem> {
    const entries = weeklyAvailability
        .map((item) => ({
            id: item.id,
            status: item.status,
            source: item.source,
            sourceLabel: getAvailabilitySourceLabel(
                item.source,
                item.source_system,
                externalSourceLabel,
            ),
            startMinuteOfWeek: item.start_minute_of_week,
            endMinuteOfWeek: item.end_minute_of_week,
        }))
        .sort(
            (a, b) =>
                a.startMinuteOfWeek - b.startMinuteOfWeek ||
                a.endMinuteOfWeek - b.endMinuteOfWeek,
        );

    const groups: Array<{
        status: AvailabilityStatus;
        startMinuteOfWeek: number;
        endMinuteOfWeek: number;
        entries: Array<WeeklyAvailabilityEntry>;
    }> = [];

    entries.forEach((entry) => {
        const current = groups[groups.length - 1];
        if (
            current &&
            current.status === entry.status &&
            entry.startMinuteOfWeek <= current.endMinuteOfWeek
        ) {
            current.endMinuteOfWeek = Math.max(
                current.endMinuteOfWeek,
                entry.endMinuteOfWeek,
            );
            current.entries.push(entry);
            return;
        }

        groups.push({
            status: entry.status,
            startMinuteOfWeek: entry.startMinuteOfWeek,
            endMinuteOfWeek: entry.endMinuteOfWeek,
            entries: [entry],
        });
    });

    if (groups.length > 1) {
        const first = groups[0]!;
        const last = groups[groups.length - 1]!;
        if (
            first.startMinuteOfWeek === 0 &&
            last.endMinuteOfWeek === MINUTES_PER_WEEK &&
            first.status === last.status
        ) {
            const combined = {
                status: last.status,
                startMinuteOfWeek: last.startMinuteOfWeek,
                endMinuteOfWeek: MINUTES_PER_WEEK + first.endMinuteOfWeek,
                entries: [...last.entries, ...first.entries],
            };
            groups.splice(groups.length - 1, 1);
            groups.shift();
            groups.push(combined);
        }
    }

    return groups.map((group) =>
        buildWeeklyItem(
            group.entries,
            group.startMinuteOfWeek,
            group.endMinuteOfWeek,
        ),
    );
}

/**
 * Resolve a caregiver's effective availability windows for the calendar:
 * one-time entries plus weekly pattern rules expanded into concrete
 * occurrences for every week overlapping [rangeStartDateKey,
 * rangeEndDateKey] (inclusive), merged in a single pass in which dated
 * entries always override recurring occurrences — the same precedence the
 * recommendation engine applies. Occurrence times are resolved per week in
 * the organization timezone, so a "Mon 9:00" rule stays 9:00 local across
 * DST transitions.
 */
export function buildEffectiveAvailabilityItems(
    availability: ReadonlyArray<RawTimeboxedAvailability>,
    weeklyAvailability: ReadonlyArray<RawWeeklyAvailability>,
    externalSourceLabel: string | undefined,
    rangeStartDateKey: string,
    rangeEndDateKey: string,
    timezone: string,
): Array<TimeboxedAvailabilityItem> {
    const entries: Array<TimeboxedAvailabilityEntry> = availability.map(
        (item) => ({
            id: item.id,
            status: item.status,
            source: item.source,
            sourceLabel: getAvailabilitySourceLabel(
                item.source,
                item.source_system,
                externalSourceLabel,
            ),
            startTime: item.start_time,
            endTime: item.end_time,
        }),
    );

    const rangeEndExclusiveKey = shiftDateKey(rangeEndDateKey, 1);
    for (
        let weekStartKey = mondayStartDateKey(rangeStartDateKey);
        weekStartKey < rangeEndExclusiveKey;
        weekStartKey = shiftDateKey(weekStartKey, 7)
    ) {
        weeklyAvailability.forEach((rule) => {
            entries.push({
                // Week-scoped ids keep each occurrence distinct so the
                // interval merge does not collapse repeats across weeks.
                id: `${rule.id}:${weekStartKey}`,
                status: rule.status,
                source: rule.source,
                sourceLabel: getAvailabilitySourceLabel(
                    rule.source,
                    rule.source_system,
                    externalSourceLabel,
                ),
                recurring: true,
                startTime: minuteOfWeekToInstant(
                    weekStartKey,
                    rule.start_minute_of_week,
                    timezone,
                ),
                endTime: minuteOfWeekToInstant(
                    weekStartKey,
                    rule.end_minute_of_week,
                    timezone,
                ),
            });
        });
    }

    return mergeTimeboxedAvailabilityEntries(entries);
}

function minuteOfWeekToInstant(
    weekStartDateKey: string,
    minuteOfWeek: number,
    timezone: string,
): string {
    const dayKey = shiftDateKey(
        weekStartDateKey,
        Math.floor(minuteOfWeek / MINUTES_PER_DAY),
    );
    const minuteOfDay = minuteOfWeek % MINUTES_PER_DAY;
    const hours = String(Math.floor(minuteOfDay / 60)).padStart(2, '0');
    const minutes = String(minuteOfDay % 60).padStart(2, '0');
    return fromZonedTime(
        `${dayKey}T${hours}:${minutes}:00`,
        timezone,
    ).toISOString();
}

/**
 * Split merged weekly items into per-day segments for the week time grid,
 * with cross-source conflicts marked. Day boundaries (including the
 * Sunday-to-Monday wrap) come from the shared segment builder.
 */
export function buildWeeklyGridSegments(
    items: ReadonlyArray<WeeklyItem>,
    weekStartDateKey: string,
    timezone: string,
): Array<WeeklyScheduleSegment> {
    return withWeeklyConflictState(
        items
            .flatMap((item) =>
                buildWeeklySegments({
                    item,
                    weekStartDateKey,
                    timezone,
                }),
            )
            .sort((a, b) => a.sortMinuteOfWeek - b.sortMinuteOfWeek),
    );
}

function buildWeeklyItem(
    entries: ReadonlyArray<WeeklyAvailabilityEntry>,
    startMinuteOfWeek: number,
    endMinuteOfWeek: number,
): WeeklyItem {
    return {
        id: `weekly-${entries.map((entry) => entry.id).join('-')}`,
        status: entries[0]?.status ?? 'available',
        sourceLabel: getCollapsedSourceLabel(entries),
        hasConflict: hasAvailabilityStatusConflict(entries),
        startMinuteOfWeek,
        endMinuteOfWeek,
        entries,
    };
}

function hasAvailabilityStatusConflict(
    entries: ReadonlyArray<{ readonly status: AvailabilityStatus }>,
): boolean {
    return new Set(entries.map((entry) => entry.status)).size > 1;
}

function weeklyEntryOverlapsSegment(
    entry: WeeklyAvailabilityEntry,
    segmentStartMinuteOfWeek: number,
    segmentEndMinuteOfWeek: number,
): boolean {
    const ranges = [
        {
            start: entry.startMinuteOfWeek,
            end: entry.endMinuteOfWeek,
        },
        {
            start: entry.startMinuteOfWeek + MINUTES_PER_WEEK,
            end: entry.endMinuteOfWeek + MINUTES_PER_WEEK,
        },
    ];

    return ranges.some(
        (range) =>
            range.start < segmentEndMinuteOfWeek &&
            range.end > segmentStartMinuteOfWeek,
    );
}

function buildWeeklyEntrySegments({
    entries,
    segmentStartMinuteOfWeek,
    segmentEndMinuteOfWeek,
    weekStartDateKey,
    timezone,
}: {
    readonly entries: ReadonlyArray<WeeklyAvailabilityEntry>;
    readonly segmentStartMinuteOfWeek: number;
    readonly segmentEndMinuteOfWeek: number;
    readonly weekStartDateKey: string;
    readonly timezone: string;
}): Array<WeeklyAvailabilityEntrySegment> {
    return entries
        .map((entry) => {
            const ranges = [
                {
                    start: entry.startMinuteOfWeek,
                    end: entry.endMinuteOfWeek,
                },
                {
                    start: entry.startMinuteOfWeek + MINUTES_PER_WEEK,
                    end: entry.endMinuteOfWeek + MINUTES_PER_WEEK,
                },
            ];
            const overlap = ranges.find(
                (range) =>
                    range.start < segmentEndMinuteOfWeek &&
                    range.end > segmentStartMinuteOfWeek,
            );
            if (!overlap) {
                return null;
            }

            const startMinuteOfWeek = Math.max(
                overlap.start,
                segmentStartMinuteOfWeek,
            );
            const endMinuteOfWeek = Math.min(
                overlap.end,
                segmentEndMinuteOfWeek,
            );

            return {
                id: `${entry.id}-${startMinuteOfWeek}-${endMinuteOfWeek}`,
                entry,
                status: entry.status,
                source: entry.source,
                sourceLabel: entry.sourceLabel,
                dayLabel: getWeeklyDayLabel(startMinuteOfWeek),
                startMinuteOfWeek,
                endMinuteOfWeek,
                timeLabel: formatWeeklyTimeRange(
                    startMinuteOfWeek,
                    endMinuteOfWeek,
                    weekStartDateKey,
                    timezone,
                ),
            };
        })
        .filter(
            (segment): segment is WeeklyAvailabilityEntrySegment =>
                segment !== null,
        );
}

function buildWeeklySegments({
    item,
    weekStartDateKey,
    timezone,
}: {
    readonly item: WeeklyItem;
    readonly weekStartDateKey: string;
    readonly timezone: string;
}): Array<WeeklyScheduleSegment> {
    const segments: Array<WeeklyScheduleSegment> = [];
    let cursor = item.startMinuteOfWeek;

    while (cursor < item.endMinuteOfWeek) {
        const rawDayIndex = Math.floor(cursor / MINUTES_PER_DAY);
        const segmentEnd = Math.min(
            item.endMinuteOfWeek,
            (rawDayIndex + 1) * MINUTES_PER_DAY,
        );
        const dayIndex = ((rawDayIndex % 7) + 7) % 7;
        const entries = item.entries.filter((entry) =>
            weeklyEntryOverlapsSegment(entry, cursor, segmentEnd),
        );
        segments.push({
            id: `${weekStartDateKey}-${item.id}-${cursor}-${segmentEnd}`,
            parentId: item.id,
            status: item.status,
            sourceLabel: getCollapsedSourceLabel(entries),
            hasConflict: item.hasConflict,
            dayIndex,
            dayLabel: DAY_NAMES[dayIndex] || 'Monday',
            startMinuteOfWeek: cursor,
            endMinuteOfWeek: segmentEnd,
            timeLabel: formatWeeklyTimeRange(
                cursor,
                segmentEnd,
                weekStartDateKey,
                timezone,
            ),
            sortMinuteOfWeek:
                dayIndex * MINUTES_PER_DAY + (cursor % MINUTES_PER_DAY),
            entries,
            entrySegments: buildWeeklyEntrySegments({
                entries,
                segmentStartMinuteOfWeek: cursor,
                segmentEndMinuteOfWeek: segmentEnd,
                weekStartDateKey,
                timezone,
            }),
        });
        cursor = segmentEnd;
    }

    return segments;
}

function withWeeklyConflictState(
    segments: ReadonlyArray<WeeklyScheduleSegment>,
): Array<WeeklyScheduleSegment> {
    return segments.map((segment) => ({
        ...segment,
        hasConflict: segments.some(
            (other) =>
                other.id !== segment.id &&
                other.dayIndex === segment.dayIndex &&
                other.status !== segment.status &&
                weeklySegmentsOverlap(segment, other),
        ),
    }));
}

function weeklySegmentsOverlap(
    first: WeeklyScheduleSegment,
    second: WeeklyScheduleSegment,
): boolean {
    const firstRange = getWeeklySegmentMinuteOfDayRange(first);
    const secondRange = getWeeklySegmentMinuteOfDayRange(second);

    return (
        firstRange.start < secondRange.end && firstRange.end > secondRange.start
    );
}

function getWeeklySegmentStartMinuteOfDay(
    segment: WeeklyScheduleSegment,
): number {
    return segment.startMinuteOfWeek % MINUTES_PER_DAY;
}

function getWeeklySegmentMinuteOfDayRange(segment: WeeklyScheduleSegment): {
    readonly start: number;
    readonly end: number;
} {
    const start = getWeeklySegmentStartMinuteOfDay(segment);
    return {
        start,
        end: start + getWeeklySegmentDuration(segment),
    };
}

function getWeeklySegmentDuration(segment: WeeklyScheduleSegment): number {
    return segment.endMinuteOfWeek - segment.startMinuteOfWeek;
}

function getWeeklyDayLabel(startMinuteOfWeek: number): string {
    const dayIndex =
        ((Math.floor(startMinuteOfWeek / MINUTES_PER_DAY) % 7) + 7) % 7;
    return DAY_NAMES[dayIndex] || 'Monday';
}
